import React, { useRef, useEffect } from 'react';

export type DobValue = {
  month: string;
  day: string;
  year: string;
};

export type ClinicDobInputProps = {
  label?: string;
  value: DobValue;
  onChange: (field: 'month' | 'day' | 'year', value: string) => void;
  error?: string;
  className?: string;
};

export const ClinicDobInput: React.FC<ClinicDobInputProps> = ({
  label = 'Date of birth',
  value,
  onChange,
  error,
  className = '',
}) => {
  const monthInputRef = useRef<HTMLInputElement>(null);
  const dayInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const baseInputClass = `placeholder-clinic-text px-0 py-1 bg-transparent border-0 border-b focus:outline-none focus:ring-0`;
  const borderColor = error ? 'red' : 'rgba(255,255,255,0.6)';

  // Auto-focus to next field when 2 digits are entered
  useEffect(() => {
    if (value.month.length === 2 && monthInputRef.current === document.activeElement) {
      dayInputRef.current?.focus();
    }
  }, [value.month]);

  useEffect(() => {
    if (value.day.length === 2 && dayInputRef.current === document.activeElement) {
      yearInputRef.current?.focus();
    }
  }, [value.day]);

  return (
    <div className={className}>
      <label
        className='block text-sm font-medium mb-4'
        style={{ color: 'var(--clinic-text-secondary)' }}
      >
        {label}
      </label>

      <div className='flex flex-col md:flex-row items-start md:items-end gap-4 md:gap-6'>
        <div className='flex flex-col'>
          <span
            className='text-xs mb-2'
            style={{ color: 'var(--clinic-text)' }}
          >
            Month
          </span>
          <input
            ref={monthInputRef}
            inputMode='numeric'
            pattern='[0-9]*'
            maxLength={2}
            placeholder='MM'
            value={value.month}
            onChange={e => onChange('month', e.target.value.replace(/\D/g, ''))}
            className={baseInputClass}
            style={{
              color: 'var(--clinic-text)',
              borderBottomColor: borderColor,
            }}
          />
        </div>

        <span
          className='hidden md:inline'
          style={{ color: 'var(--clinic-text)' }}
        >
          /
        </span>

        <div className='flex flex-col'>
          <span
            className='text-xs mb-2'
            style={{ color: 'var(--clinic-text)' }}
          >
            Day
          </span>
          <input
            ref={dayInputRef}
            inputMode='numeric'
            pattern='[0-9]*'
            maxLength={2}
            placeholder='DD'
            value={value.day}
            onChange={e => onChange('day', e.target.value.replace(/\D/g, ''))}
            className={baseInputClass}
            style={{
              color: 'var(--clinic-text)',
              borderBottomColor: borderColor,
            }}
          />
        </div>

        <span
          className='hidden md:inline'
          style={{ color: 'var(--clinic-text)' }}
        >
          /
        </span>

        <div className='flex flex-col'>
          <span
            className='text-xs mb-2'
            style={{ color: 'var(--clinic-text)' }}
          >
            Year
          </span>
          <input
            ref={yearInputRef}
            inputMode='numeric'
            pattern='[0-9]*'
            maxLength={4}
            placeholder='YYYY'
            value={value.year}
            onChange={e => onChange('year', e.target.value.replace(/\D/g, ''))}
            className={baseInputClass}
            style={{
              color: 'var(--clinic-text)',
              borderBottomColor: borderColor,
            }}
          />
        </div>
      </div>

      {error && <p className='text-red-500 text-sm mt-2'>{error}</p>}
    </div>
  );
};

export default ClinicDobInput;
